/**
 * ProjectStatsGrid Component
 *
 * Displays project statistics in a grid of metric cards.
 * Shows unit stats, job stats, and shared permit stats.
 */

import { Card, CardContent } from '@/components/ui/card';
import {
  Grid3X3,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Send,
  Building2,
} from 'lucide-react';
import type { UnitStats, SharedPermitStats } from '@/types/project';

interface ProjectStatsGridProps {
  totalUnits: number;
  unitStats: UnitStats;
  sharedPermitStats: SharedPermitStats;
  totalJobs: number;
}

interface StatCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  subLabel?: string;
  color?: 'default' | 'green' | 'yellow' | 'blue' | 'red';
}

function StatCard({ icon: Icon, label, value, subLabel, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'bg-muted/50',
    green: 'bg-green-50 dark:bg-green-950/30',
    yellow: 'bg-yellow-50 dark:bg-yellow-950/30',
    blue: 'bg-blue-50 dark:bg-blue-950/30',
    red: 'bg-red-50 dark:bg-red-950/30',
  };

  const iconColors = {
    default: 'text-muted-foreground',
    green: 'text-green-600 dark:text-green-400',
    yellow: 'text-yellow-600 dark:text-yellow-400',
    blue: 'text-blue-600 dark:text-blue-400',
    red: 'text-red-600 dark:text-red-400',
  };

  return (
    <Card className={colorClasses[color]}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-background/80`}>
            <Icon className={`h-5 w-5 ${iconColors[color]}`} />
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
            {subLabel && (
              <p className="text-xs text-muted-foreground">{subLabel}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProjectStatsGrid({
  totalUnits,
  unitStats,
  sharedPermitStats,
  totalJobs,
}: ProjectStatsGridProps) {
  // Calculate percentages
  const readyPercent = totalUnits > 0
    ? Math.round((unitStats.ready / totalUnits) * 100)
    : 0;
  const issuedPercent = totalUnits > 0
    ? Math.round((unitStats.issued / totalUnits) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Main Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Grid3X3}
          label="Total Units"
          value={totalUnits}
          color="default"
        />
        <StatCard
          icon={Clock}
          label="Ready to Submit"
          value={unitStats.ready}
          subLabel={`${readyPercent}% of units`}
          color="blue"
        />
        <StatCard
          icon={AlertCircle}
          label="Blocked"
          value={unitStats.blocked}
          color={unitStats.blocked > 0 ? 'yellow' : 'default'}
        />
        <StatCard
          icon={CheckCircle}
          label="Permits Issued"
          value={unitStats.issued}
          subLabel={`${issuedPercent}% complete`}
          color="green"
        />
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label="Total Jobs"
          value={totalJobs}
          color="default"
        />
        <StatCard
          icon={Send}
          label="Submitted"
          value={unitStats.submitted}
          color="blue"
        />
        <StatCard
          icon={Building2}
          label="Shared Permits"
          value={sharedPermitStats.total}
          subLabel={`${sharedPermitStats.issued} issued`}
          color="default"
        />
        <StatCard
          icon={AlertCircle}
          label="Shared Blocked"
          value={sharedPermitStats.blocked}
          color={sharedPermitStats.blocked > 0 ? 'red' : 'default'}
        />
      </div>
    </div>
  );
}
